import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import dayjs, { Dayjs } from 'dayjs';
import { MachineDashboardWidgetSection } from '../types/MachineDashboard.types';
import {
  BatteryDischargeStatus,
  DataStatus,
  FleetOperatingTime,
  MachineGeofenceStatus,
  MachineClassification,
  MachineUtilization,
  ManualMachineDashboard,
} from 'app/cross-cutting-concerns/communication/interfaces/am-api-graphql';
import { TotalMachineOperatingHoursDatum } from 'app/modules/cleaning/widgets/machine-dashboard/components/MachineOperatingHoursDashboardChart/interfaces/MachineOperatingHoursDashboardChart.types';
import { CleaningWidgetsFleetDashboardActions } from 'app/modules/cleaning/widgets/machine-dashboard/state/CleaningWidgetsFleetDashboard/cleaningWidgetsFleetDashboardActions';
import { CleaningWidgetsFleetDashboardSelectors } from 'app/modules/cleaning/widgets/machine-dashboard/state/CleaningWidgetsFleetDashboard/cleaningWidgetsFleetDashboardSelectors';
import { FleetOperatingTimeActions } from 'app/modules/cleaning/widgets/machine-dashboard/state/FleetOperatingHoursChart/fleetOperatingTimeActions';
import { FleetOperatingTimeSelectors } from 'app/modules/cleaning/widgets/machine-dashboard/state/FleetOperatingHoursChart/fleetOperatingTimeSelectors';
import { SiteWidgetsFleetDashboardActions } from 'app/modules/site-management/widgets/fleet-dashboard/state/siteWidgetsFleetDashboardActions';
import { SiteWidgetsFleetDashboardSelectors } from 'app/modules/site-management/widgets/fleet-dashboard/state/siteWidgetsFleetDashboardSelectors';
import {
  SitePerformanceChartUtils,
  SitePerformanceChartData,
} from 'app/modules/site-management/widgets/fleet-dashboard/utils/SitePerformanceChartUtils';
import { OperatingHoursChartUtils } from 'app/modules/cleaning/utils/OperatingHoursChartUtils';
import { OpenSearch } from 'config/constants';
import { CleaningReportRequestDateRangeRestrictor } from 'app/modules/cleaning/CleaningReportRequestDateRangeRestrictor';
import { Optional } from 'lib/types/Optional';
import { MachineDashboardActions } from 'app/modules/cleaning/widgets/machine-dashboard/state/MachineDashboard/machineDashboardSlice';
import { MachineDashboardSelectors } from 'app/modules/cleaning/widgets/machine-dashboard/state/MachineDashboard/machineDashboardSelectors';
import { MachineListActions } from 'app/modules/machine-inventory/machine-list/state/machineListActions';
import {
  PERIOD_DEPENDENT_MACHINE_DASHBOARD_WIDGET_IDS,
  PERIOD_INDEPENDENT_MACHINE_DASHBOARD_WIDGET_IDS,
} from 'app/modules/cleaning/widgets/machine-dashboard/types/machineDashboardWidgetListState.types';
import { RoutePaths } from 'config/route-paths';
import { SiteWithOperatingTime } from 'app/modules/site-management/interfaces/Site.types';

export interface MachineDashboardContextValue {
  startDateCleaningWidgets: Optional<string>;
  endDateCleaningWidgets: Optional<string>;
  dateFilterPresets: { label: string; value: [Dayjs, Dayjs] }[];
  fleetOperatingHoursChartData: Optional<FleetOperatingTime>;
  isFleetOperatingHoursChartLoading: boolean;
  machineOperatingHoursDashboardChartData: TotalMachineOperatingHoursDatum[];
  isMachineOperatingTimeLoading: boolean;
  sitePerformanceChartData: SitePerformanceChartData[];
  isSitesLoading: boolean;
  disabledDate: typeof CleaningReportRequestDateRangeRestrictor.disabledDatePredicate;
  handleDateChange: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  manualMachineDashboardData: Optional<ManualMachineDashboard>;
  isManualMachineDashboardLoading: boolean;
  /** Loading for data status / geofencing / battery — not tied to period changes. */
  isPeriodIndependentKpisLoading: boolean;
  handleSiteFilterChange: (siteIds: string[] | undefined, machineIds: string[] | undefined) => void;
  handleNavigateToDeviceTable: (widgetSection: MachineDashboardWidgetSection) => void;
  filterSitesData: Optional<{ id: string; name: string }[]>;
  sitesData: Optional<SiteWithOperatingTime[]>;
  noSiteMachines: Optional<{ id: string; name: string }[]>;
  isSiteFilterLoading: boolean;
}

export const useMachineDashboardState = (): MachineDashboardContextValue => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [selectedSiteIds, setSelectedSiteIds] = useState<string[] | undefined>(undefined);
  const [selectedMachineIds, setSelectedMachineIds] = useState<string[] | undefined>(undefined);

  const handleSiteFilterChange = useCallback(
    (siteIds: string[] | undefined, machineIds: string[] | undefined): void => {
      setSelectedSiteIds(siteIds);
      setSelectedMachineIds(machineIds);
    },
    []
  );

  useEffect(() => {
    dispatch(
      CleaningWidgetsFleetDashboardActions.setPeriodFilter({
        startDate: dayjs().startOf('week').startOf('day').toISOString(),
        endDate: dayjs().endOf('day').toISOString(),
      })
    );
  }, [dispatch]);

  const startDateCleaningWidgets = useSelector(CleaningWidgetsFleetDashboardSelectors.selectPeriodStartDate);
  const endDateCleaningWidgets = useSelector(CleaningWidgetsFleetDashboardSelectors.selectPeriodEndDate);
  const startDateSiteWidgets = useSelector(SiteWidgetsFleetDashboardSelectors.selectPeriodStartDate);
  const endDateSiteWidgets = useSelector(SiteWidgetsFleetDashboardSelectors.selectPeriodEndDate);
  const startDateFleetOperatingTime = useSelector(FleetOperatingTimeSelectors.selectPeriodStartDate);
  const endDateFleetOperatingTime = useSelector(FleetOperatingTimeSelectors.selectPeriodEndDate);

  const sitesData = useSelector(SiteWidgetsFleetDashboardSelectors.selectData);
  const isSitesLoading = !!useSelector(SiteWidgetsFleetDashboardSelectors.selectIsLoading);
  const noSiteMachinesOperatingTime = useSelector(SiteWidgetsFleetDashboardSelectors.selectNoSiteMachinesOperatingTime);
  const filterSitesData = useSelector(MachineDashboardSelectors.selectSitesListData);
  const noSiteMachines = useSelector(MachineDashboardSelectors.selectNoSiteMachinesData);
  const isFilterSitesLoading = !!useSelector(MachineDashboardSelectors.selectSitesListIsLoading);
  const isNoSiteMachinesLoading = !!useSelector(MachineDashboardSelectors.selectNoSiteMachinesIsLoading);
  const isSiteFilterLoading = isFilterSitesLoading || isNoSiteMachinesLoading;

  // undefined selectedMachineIds means "untouched default" — include every no-site machine.
  const resolvedNoSiteMachineIds = useMemo(
    () => selectedMachineIds ?? (noSiteMachines ?? []).map(machine => machine.id),
    [selectedMachineIds, noSiteMachines]
  );

  const sitePerformanceChartData = useMemo(
    () =>
      SitePerformanceChartUtils.convertSitesToChartData(
        sitesData ?? [],
        noSiteMachinesOperatingTime,
        t('overview.fleetDashboard.sitePerformanceChart.unassignedMachines')
      ),
    [sitesData, noSiteMachinesOperatingTime, t]
  );

  const fleetOperatingHoursChartData = useSelector(FleetOperatingTimeSelectors.selectData);
  const isFleetOperatingHoursChartLoading = !!useSelector(FleetOperatingTimeSelectors.selectIsLoading);

  const machineOperatingHoursData = useSelector(CleaningWidgetsFleetDashboardSelectors.selectData);
  const machineOperatingHoursDashboardChartData = useMemo(
    (): TotalMachineOperatingHoursDatum[] => machineOperatingHoursData ?? [],
    [machineOperatingHoursData]
  );
  const isMachineOperatingTimeLoading = !!useSelector(CleaningWidgetsFleetDashboardSelectors.selectIsLoading);

  const manualMachineDashboardData = useSelector(MachineDashboardSelectors.selectManualMachineDashboardData);
  const isManualMachineDashboardLoading = !!useSelector(
    MachineDashboardSelectors.selectManualMachineDashboardIsLoading
  );
  const isPeriodIndependentWidgetListsLoading = !!useSelector(
    MachineDashboardSelectors.selectIsPeriodIndependentWidgetListsLoading
  );
  // Initial load / site filter: show skeletons. Period-only refetch does not touch these widget lists.
  const isPeriodIndependentKpisLoading = !manualMachineDashboardData || isPeriodIndependentWidgetListsLoading;

  const dateFilterPresets = useMemo(() => {
    const today = dayjs();
    const capEnd = (date: Dayjs): Dayjs => (date.isAfter(today, 'day') ? today.endOf('day') : date.endOf('day'));

    const currentQuarterMonthStart = Math.floor(today.month() / 3) * 3;
    const thisQuarterStart = today.month(currentQuarterMonthStart).startOf('month').startOf('day');
    const thisQuarterEnd = thisQuarterStart.add(2, 'month').endOf('month');
    const lastQuarterStart = thisQuarterStart.subtract(3, 'month').startOf('day');
    const lastQuarterEnd = lastQuarterStart.add(2, 'month').endOf('month').endOf('day');

    return [
      {
        label: t('machineDashboard.dateFilter.thisWeek').toLocaleLowerCase(i18n.language),
        value: [today.startOf('week').startOf('day'), capEnd(today.endOf('week'))] as [Dayjs, Dayjs],
      },
      {
        label: t('machineDashboard.dateFilter.lastWeek').toLocaleLowerCase(i18n.language),
        value: [
          today.subtract(1, 'week').startOf('week').startOf('day'),
          today.subtract(1, 'week').endOf('week').endOf('day'),
        ] as [Dayjs, Dayjs],
      },
      {
        label: t('machineDashboard.dateFilter.thisMonth').toLocaleLowerCase(i18n.language),
        value: [today.startOf('month').startOf('day'), capEnd(today.endOf('month'))] as [Dayjs, Dayjs],
      },
      {
        label: t('machineDashboard.dateFilter.lastMonth').toLocaleLowerCase(i18n.language),
        value: [
          today.subtract(1, 'month').startOf('month').startOf('day'),
          today.subtract(1, 'month').endOf('month').endOf('day'),
        ] as [Dayjs, Dayjs],
      },
      {
        label: t('machineDashboard.dateFilter.thisQuarter').toLocaleLowerCase(i18n.language),
        value: [thisQuarterStart, capEnd(thisQuarterEnd)] as [Dayjs, Dayjs],
      },
      {
        label: t('machineDashboard.dateFilter.lastQuarter').toLocaleLowerCase(i18n.language),
        value: [lastQuarterStart, lastQuarterEnd] as [Dayjs, Dayjs],
      },
      {
        label: t('machineDashboard.dateFilter.thisYear').toLocaleLowerCase(i18n.language),
        value: [today.startOf('year').startOf('day'), capEnd(today.endOf('year'))] as [Dayjs, Dayjs],
      },
      {
        label: t('machineDashboard.dateFilter.lastYear').toLocaleLowerCase(i18n.language),
        value: [
          today.subtract(1, 'year').startOf('year').startOf('day'),
          today.subtract(1, 'year').endOf('year').endOf('day'),
        ] as [Dayjs, Dayjs],
      },
    ];
  }, [t, i18n.language]);

  useEffect(() => {
    dispatch(
      MachineDashboardActions.machineDashboardSitesListRequest({
        paginationOptions: {
          limit: OpenSearch.MAX_RESULT_WINDOW,
        },
      })
    );
    dispatch(MachineDashboardActions.machineDashboardNoSiteMachinesRequest());
  }, [dispatch]);

  const isPeriodReady = Boolean(startDateCleaningWidgets && endDateCleaningWidgets);

  // Period-dependent KPIs (manual dashboard aggregate + machine utilisation list) refetch on period/site change.
  useEffect(() => {
    if (!startDateCleaningWidgets || !endDateCleaningWidgets) {
      return;
    }

    const period = {
      startAt: dayjs(startDateCleaningWidgets).toISOString(),
      endAt: dayjs(endDateCleaningWidgets).toISOString(),
    };

    const manualMachineDashboardFilter =
      selectedSiteIds !== undefined || selectedMachineIds !== undefined
        ? {
            ...(selectedSiteIds !== undefined && { siteIds: selectedSiteIds }),
            ...(selectedMachineIds !== undefined && { machineIds: selectedMachineIds }),
          }
        : undefined;

    dispatch(
      MachineDashboardActions.machineDashboardManualMachineDashboardRequest({
        period,
        filter: manualMachineDashboardFilter,
      })
    );

    PERIOD_DEPENDENT_MACHINE_DASHBOARD_WIDGET_IDS.forEach(widgetId => {
      dispatch(
        MachineDashboardActions.machineDashboardWidgetListRequest({
          widgetId,
          period,
          siteIds: selectedSiteIds,
          machineIds: selectedMachineIds,
        })
      );
    });
  }, [dispatch, startDateCleaningWidgets, endDateCleaningWidgets, selectedSiteIds, selectedMachineIds]);

  // Period-independent KPI lists (data status / geofencing / battery) only refetch on site filter change
  // (and once when the period filter is first available — API still requires a period argument).
  useEffect(() => {
    if (!isPeriodReady || !startDateCleaningWidgets || !endDateCleaningWidgets) {
      return;
    }

    const period = {
      startAt: dayjs(startDateCleaningWidgets).toISOString(),
      endAt: dayjs(endDateCleaningWidgets).toISOString(),
    };

    PERIOD_INDEPENDENT_MACHINE_DASHBOARD_WIDGET_IDS.forEach(widgetId => {
      dispatch(
        MachineDashboardActions.machineDashboardWidgetListRequest({
          widgetId,
          period,
          siteIds: selectedSiteIds,
          machineIds: selectedMachineIds,
        })
      );
    });
    // Intentionally omit period dates: these widgets ignore the date filter.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- period-independent fetch keyed by site filter + readiness
  }, [dispatch, selectedSiteIds, selectedMachineIds, isPeriodReady]);

  useEffect(() => {
    if (startDateFleetOperatingTime && endDateFleetOperatingTime) {
      dispatch(
        FleetOperatingTimeActions.getFleetOperatingTimeRequest({
          filter: {
            period: {
              startAt: dayjs(startDateFleetOperatingTime).toISOString(),
              endAt: dayjs(endDateFleetOperatingTime).toISOString(),
            },
          },
        })
      );
    }
  }, [dispatch, startDateFleetOperatingTime, endDateFleetOperatingTime]);

  useEffect(() => {
    if (startDateCleaningWidgets && endDateCleaningWidgets) {
      dispatch(
        CleaningWidgetsFleetDashboardActions.getCleaningListWithOperatingTimeRequest({
          filter: {
            classifications: [MachineClassification.Gcd, MachineClassification.Robot],
            isIoTDevice: true,
            siteIds: selectedSiteIds,
            ids: selectedMachineIds,
          },
          period: {
            startAt: dayjs(startDateCleaningWidgets).toISOString(),
            endAt: dayjs(endDateCleaningWidgets).toISOString(),
          },
          paginationOptions: {
            limit: OpenSearch.MAX_RESULT_WINDOW,
          },
        })
      );
    }
  }, [dispatch, startDateCleaningWidgets, endDateCleaningWidgets, selectedSiteIds, selectedMachineIds]);

  useEffect(() => {
    if (startDateSiteWidgets && endDateSiteWidgets) {
      dispatch(
        SiteWidgetsFleetDashboardActions.getSiteListWithOperatingTimeRequest({
          period: {
            startAt: dayjs(startDateSiteWidgets).toISOString(),
            endAt: dayjs(endDateSiteWidgets).toISOString(),
          },
          filter: {
            ids: selectedSiteIds,
          },
          paginationOptions: {
            limit: OpenSearch.MAX_RESULT_WINDOW,
          },
          noSiteMachineIds: resolvedNoSiteMachineIds,
        })
      );
    }
  }, [dispatch, startDateSiteWidgets, endDateSiteWidgets, selectedSiteIds, resolvedNoSiteMachineIds]);

  useEffect(
    () => (): void => {
      dispatch(CleaningWidgetsFleetDashboardActions.resetState());
      dispatch(FleetOperatingTimeActions.resetState());
      dispatch(MachineDashboardActions.machineDashboardManualMachineDashboardCancel());
      dispatch(MachineDashboardActions.machineDashboardWidgetListsResetState());
    },
    [dispatch]
  );

  const handleDateChange = useCallback(
    (dates: [Dayjs | null, Dayjs | null] | null): void => {
      if (dates && dates[0] && dates[1]) {
        const startDate = OperatingHoursChartUtils.prepareStartDate(dates[0].toDate()).toISOString();
        const endDate = OperatingHoursChartUtils.prepareEndDate(dates[1].toDate()).toISOString();
        dispatch(CleaningWidgetsFleetDashboardActions.setPeriodFilter({ startDate, endDate }));
        dispatch(SiteWidgetsFleetDashboardActions.setPeriodFilter({ startDate, endDate }));
        dispatch(FleetOperatingTimeActions.setPeriodFilter({ startDate, endDate }));
      }
    },
    [dispatch]
  );

  const handleNavigateToDeviceTable = useCallback(
    (widgetSection: MachineDashboardWidgetSection): void => {
      dispatch(
        MachineListActions.setActiveClassificationsFilter([MachineClassification.Gcd, MachineClassification.Robot])
      );
      dispatch(MachineListActions.setActiveIotClassificationFilter('true'));

      if (widgetSection === 'dataStatus') {
        dispatch(MachineListActions.setActiveMachineStatusFilter(DataStatus.GreaterThanThreeDaysOld));
      }

      if (widgetSection === 'geofencing') {
        dispatch(MachineListActions.setActiveGeofenceStatusFilter([MachineGeofenceStatus.Outside]));
      }

      if (widgetSection === 'batteryStatus') {
        dispatch(MachineListActions.setActiveBatteryDischargeStatusFilter([BatteryDischargeStatus.Critical]));
      }

      if (widgetSection === 'machineUtilization') {
        dispatch(MachineListActions.setActiveMachineUtilizationFilter([MachineUtilization.NotUsed]));
        dispatch(
          MachineListActions.setActiveMachinePeriodFilter({
            startAt: dayjs(startDateCleaningWidgets).toISOString(),
            endAt: dayjs(endDateCleaningWidgets).toISOString(),
          })
        );
      }

      if (selectedSiteIds !== undefined) {
        dispatch(MachineListActions.setActiveSiteFilter(selectedSiteIds));
      }

      dispatch(MachineListActions.setActiveFromMachineDashboard(true));

      navigate(RoutePaths.MACHINES);
    },
    [dispatch, navigate, startDateCleaningWidgets, endDateCleaningWidgets, selectedSiteIds]
  );

  return useMemo(
    (): MachineDashboardContextValue => ({
      startDateCleaningWidgets,
      endDateCleaningWidgets,
      dateFilterPresets,
      fleetOperatingHoursChartData,
      isFleetOperatingHoursChartLoading,
      machineOperatingHoursDashboardChartData,
      isMachineOperatingTimeLoading,
      sitePerformanceChartData,
      isSitesLoading,
      disabledDate: CleaningReportRequestDateRangeRestrictor.disabledDatePredicate,
      handleDateChange,
      manualMachineDashboardData,
      isManualMachineDashboardLoading,
      isPeriodIndependentKpisLoading,
      handleSiteFilterChange,
      handleNavigateToDeviceTable,
      filterSitesData,
      sitesData,
      noSiteMachines,
      isSiteFilterLoading,
    }),
    [
      startDateCleaningWidgets,
      endDateCleaningWidgets,
      dateFilterPresets,
      fleetOperatingHoursChartData,
      isFleetOperatingHoursChartLoading,
      machineOperatingHoursDashboardChartData,
      isMachineOperatingTimeLoading,
      sitePerformanceChartData,
      isSitesLoading,
      handleDateChange,
      manualMachineDashboardData,
      isManualMachineDashboardLoading,
      isPeriodIndependentKpisLoading,
      handleSiteFilterChange,
      handleNavigateToDeviceTable,
      filterSitesData,
      sitesData,
      noSiteMachines,
      isSiteFilterLoading,
    ]
  );
};
